import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import UploadIcon from "@mui/icons-material/Upload";
import BusinessIcon from "@mui/icons-material/Business";
import { useEffect, useState } from "react";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FullPageLoader from "../../app/shared/common/FullPageLoader";

const UploadCustomerAttachment = observer(() => {
  const { onboardingStore, toasterStore } = useStore();
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigating, setNavigating] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Fetch the outlet for this org and navigate to the update-outlet page
  const navigateToOutlet = async () => {
    if (!organisationId) return;
    setNavigating(true);
    try {
      const res = await onboardingStore.getOutletByOrgId(Number(organisationId));
      if (res?.status_code === 1 && res.data?.length > 0) {
        const outlet = res.data[0];
        navigate(`/customer/update-outlet/${organisationId}/${outlet.id}`, {
          state: location.state
        });
      } else {
        // Fallback: go to customer list if no outlet found
        navigate("/customer");
      }
    } catch {
      navigate("/customer");
    } finally {
      setNavigating(false);
    }
  };

  const handleUploadAll = async () => {
    if (!organisationId) return;

    if (imageFile) {
      const formData = new FormData();
      formData.append("id", String(organisationId));
      formData.append("image", imageFile);

      setUploading(true);
      try {
        await onboardingStore.uploadOrganisationImage(formData);
        toasterStore.success("Image uploaded successfully");
      } catch (error: any) {
        const msg =
          error?.response?.data?.msg ||
          "Something went wrong. Please try again.";
        toasterStore.error(msg);
      } finally {
        setUploading(false);
      }
    }

    await navigateToOutlet();
  };

  const handleSkip = async () => {
    await navigateToOutlet();
  };

  return (
    <Box>
      <FullPageLoader
        open={uploading || navigating}
        message={uploading ? "Uploading image..." : "Please wait..."}
      />
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          mb: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Upload Organisation Image
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Organisation created successfully! Please upload documents.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Accordion
        defaultExpanded
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          "&:before": {
            display: "none",
          },
          mb: 3,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderRadius: 3,
            "&:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ImageIcon />
            <Typography variant="h6" fontWeight={600}>
              Organisation Image
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: { xs: 0, md: 2 } }}>
            {!imagePreview ? (
              <Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="organisation-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="organisation-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{
                      py: 2,
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      "&:hover": {
                        borderStyle: "dashed",
                        backgroundColor: alpha("#667eea", 0.05),
                      },
                    }}
                  >
                    Upload Organisation Image
                  </Button>
                </label>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: "divider",
                  mb: 3,
                }}
              >
                <img
                  src={imagePreview}
                  alt="Organisation Preview"
                  style={{
                    width: "100%",
                    maxHeight: 300,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                <IconButton
                  onClick={handleImageDelete}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={handleSkip}
                disabled={navigating}
                sx={{ textTransform: "none", px: 1 }}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadIcon />}
                onClick={handleUploadAll}
                disabled={!imagePreview || navigating}
                sx={{
                  textTransform: "none",
                  px: 4,
                }}
              >
                {navigating ? "Please wait..." : "Upload & Finish"}
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
});

export default UploadCustomerAttachment;
